import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AppResult } from '../core/result/appResult';
import type { UserProfile } from '../core/model/search';
import type { Session, TelegramLink, TelegramLinkState } from '../data/repository/repositories';
import { useContainer } from './ContainerContext';

interface SessionValue {
  readonly session: Session;
  readonly user: UserProfile | null;
  startTelegramLink(name: string, returnUrl?: string): Promise<AppResult<TelegramLink>>;
  pollTelegramLink(token: string): Promise<AppResult<TelegramLinkState>>;
  identify(name: string, phone: string): Promise<AppResult<UserProfile>>;
  logout(): void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { auth } = useContainer();
  const [session, setSession] = useState<Session>(() => auth.session());

  useEffect(() => auth.subscribe(setSession), [auth]);

  const startTelegramLink = useCallback(
    (name: string, returnUrl?: string) => auth.startTelegramLink(name, returnUrl),
    [auth],
  );
  const pollTelegramLink = useCallback((token: string) => auth.pollTelegramLink(token), [auth]);
  const identify = useCallback(
    (name: string, phone: string) => auth.identify(name, phone),
    [auth],
  );
  const logout = useCallback(() => auth.logout(), [auth]);

  const value = useMemo<SessionValue>(
    () => ({
      session,
      user: session.kind === 'AUTHENTICATED' ? session.user : null,
      startTelegramLink,
      pollTelegramLink,
      identify,
      logout,
    }),
    [session, startTelegramLink, pollTelegramLink, identify, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession debe usarse dentro de <SessionProvider>');
  return value;
}
